import type { FC, FormEvent, KeyboardEvent, ReactNode } from 'react'

import { useState } from 'react'
import { ArrowUp, Sparkles } from 'lucide-react'
import { AnnouncementPill } from '@/components/landing/announcement-pill'
import { RotatingPlaceholder } from '@/components/landing/rotating-placeholder'
import { useChat } from '@/components/generals/chat-assistant'
import { pilotPromptExamples } from '@/constants/pilot'

const PromptBox: FC = (): ReactNode => {
    const { ask } = useChat()
    const [value, setValue] = useState('')
    const [exampleIndex, setExampleIndex] = useState(0)

    const send = () => {
        ask(value.trim() || pilotPromptExamples[exampleIndex])
        setValue('')
    }

    const submit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        send()
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            send()
        }
    }

    return (
        <form onSubmit={submit} className='fc-hero-in relative mx-auto w-full max-w-[660px] text-left' style={{ animationDelay: '0.75s' }}>
            <span aria-hidden='true' className='fc-input-bloom' style={{ borderRadius: 48 }} />

            <div className='relative rounded-[26px] bg-white px-5 pb-3.5 pt-4 shadow-[0_1px_2px_rgba(14,14,14,0.05),0_22px_48px_-26px_rgba(54,30,140,0.5)] ring-1 ring-[#e4e2ee] transition-shadow focus-within:ring-2 focus-within:ring-violet'>
                <label htmlFor='pilot-prompt' className='inline-flex items-center gap-1.5 text-[13px] font-semibold text-violet'>
                    <Sparkles className='size-3.5' aria-hidden='true' />
                    What do you want to automate?
                </label>

                <div className='relative mt-2.5 h-[78px] sm:h-[84px]'>
                    <textarea
                        id='pilot-prompt'
                        value={value}
                        onChange={event => setValue(event.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={3}
                        className='absolute inset-0 h-full w-full resize-none bg-transparent text-[16px] leading-[1.6] text-ink focus:outline-none'
                    />

                    <RotatingPlaceholder
                        items={pilotPromptExamples}
                        hidden={value.length > 0}
                        interval={3800}
                        onIndexChange={setExampleIndex}
                        className='inset-0 text-[16px] leading-[1.6]'
                    />
                </div>

                <div className='mt-2 flex items-center justify-between gap-4'>
                    <span className='hidden text-[12.5px] text-silver sm:block'>
                        Enter to send. Shift and Enter for a new line.
                    </span>

                    <button
                        type='submit'
                        aria-label='Send to Pilot'
                        className='ml-auto inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-violet text-white shadow-[0_6px_14px_-6px_rgba(93,45,230,0.8)] transition-colors hover:bg-violet-bright focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet'
                    >
                        <ArrowUp className='size-[18px]' aria-hidden='true' />
                    </button>
                </div>
            </div>
        </form>
    )
}

export const PilotHero: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='pilot-hero-title' className='relative -mt-16 overflow-hidden pt-16'>
            <div aria-hidden='true' className='fc-hero-glow' />

            <div className='fc-container relative flex flex-col items-center pb-16 pt-16 text-center sm:pt-20 md:pb-20 md:pt-28'>
                <div className='fc-hero-in max-w-full'>
                    <AnnouncementPill />
                </div>

                <h1 id='pilot-hero-title' className='fc-hero-in mt-7 text-[clamp(2.5rem,6.6vw,4.75rem)] font-medium leading-[1.02] tracking-[-0.045em] text-ink' style={{ animationDelay: '0.18s' }}>
                    <span className='block'>Connect every tool.</span>
                    <span className='block text-silver'>Automate every handoff.</span>
                </h1>

                <p className='fc-hero-in mt-6 max-w-[560px] text-[17px] leading-relaxed text-slate md:text-lg' style={{ animationDelay: '0.45s' }}>
                    FlowCraft Pilot links your apps and uses AI to make them work together, no engineers required.
                </p>

                <div className='mt-10 w-full md:mt-12'>
                    <PromptBox />
                </div>
            </div>
        </section>
    )
}
